import { trim } from 'lodash-es';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

import ru from 'dayjs/locale/ru';
import kk from 'dayjs/locale/kk';
import minMax from 'dayjs/plugin/minMax';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import UpdateLocalPlugin from 'dayjs/plugin/updateLocale';

import { AppLocale, getLocale } from '../i18n';

const MONTH_SHORT_RU = [
  'янв',
  'фев',
  'мар',
  'апр',
  'мая',
  'июн',
  'июл',
  'авг',
  'сен',
  'окт',
  'ноя',
  'дек',
];
const MONTH_SHORT_STANDALONE_RU = [
  'янв',
  'фев',
  'мар',
  'апр',
  'май',
  'июн',
  'июл',
  'авг',
  'сен',
  'окт',
  'ноя',
  'дек',
];

const WEEKDAY_MIN_KK = ['жс', 'дс', 'сс', 'ср', 'бс', 'жм', 'сб'];

const MONTHS_IN_FORMAT = /D[oD]?(\[[^[\]]*]|\s)+MMMM?/;

const locales = {
  [AppLocale.RU]: {
    locale: ru,
    duration: {
      hour: 'ч',
      minute: 'м',
    },
  },
  [AppLocale.KK]: {
    locale: kk,
    duration: {
      hour: 'с',
      minute: 'м',
    },
  },
};

const getLocalesConfig = (locale: string) => {
  const supportedLocale = getLocale(locale);
  return locales[supportedLocale];
};

//Reference https://github.com/iamkun/dayjs/blob/dev/src/locale/ru.js#L39
const monthsShortRU = (dayjsInstance: Dayjs, format: string) => {
  if (MONTHS_IN_FORMAT.test(format)) {
    return MONTH_SHORT_RU[dayjsInstance.month()];
  }
  return MONTH_SHORT_STANDALONE_RU[dayjsInstance.month()];
};

monthsShortRU.s = MONTH_SHORT_STANDALONE_RU;
monthsShortRU.f = MONTH_SHORT_RU;

export function initDayjs(locale: string) {
  const currentLocale = getLocalesConfig(locale);

  dayjs.extend(minMax);
  dayjs.extend(isSameOrAfter);
  dayjs.extend(UpdateLocalPlugin);
  dayjs.locale(currentLocale.locale);

  dayjs.updateLocale(AppLocale.RU, {
    monthsShort: monthsShortRU,
  });

  dayjs.updateLocale(AppLocale.KK, {
    weekdaysMin: WEEKDAY_MIN_KK,
  });
}

/**
 * TODO: This type is awkward to use since it can be in form of '2024-01-23', Dayjs, etc
 * Rewrite to a correct type to be able to assign string ('2024-01-23') to it
 */
export type DateProp = string | dayjs.Dayjs | Date;

export const prettyDate = (date: DateProp, format = 'DD.MM.YYYY'): string =>
  dayjs(date).format(format);

export function secondsToHoursAndMinutes(value: string | number) {
  const { duration } = getLocalesConfig(dayjs().locale());

  const numValue = Number(value);
  const hours = Math.floor(numValue / 3600);
  const minutes = Math.floor((numValue % 3600) / 60);

  const hoursFormat = hours ? `${hours}${duration.hour}` : '';
  const minutesFormat = minutes ? `${minutes}${duration.minute}` : '';

  return trim([hoursFormat, minutesFormat].join(' '));
}

export const getTodayDate = () => dayjs().format('YYYY-MM-DD');
